import React, { useMemo, useState } from 'react'
import { PRRect } from '@sketch-hq/sketch-web-renderer'
import { TruncateWithTooltip } from '@sketch/components'

import {
  SymbolMaster,
  useFetchSymbolArtboard,
  useMapPRRectToCanvasRelativeRect,
} from '../../../hooks/inspector'

import * as S from './SymbolInstanceOverlay.styles'
import { ArtboardDetailInspectorSymbolLink } from '../../ArtboardDetailInspectorSymbolLink'
import { OVERLAYS_Z_INDEX } from '../constants'

type SymbolInstanceOverlayProps = {
  className?: string
  bounds: PRRect
  symbolMaster: SymbolMaster
  canvasContainerRef: React.RefObject<HTMLDivElement>
}

export function SymbolInstanceOverlay({
  className,
  bounds,
  symbolMaster,
  canvasContainerRef,
}: SymbolInstanceOverlayProps) {
  const {
    isLoading,
    permanentArtboardShortId,
    shareIdentifier,
  } = useFetchSymbolArtboard(symbolMaster)

  const symbolName = symbolMaster.symbolMasterShortName
    ? symbolMaster.symbolMasterShortName
    : symbolMaster.name
  const canvasRelativeRect = useMapPRRectToCanvasRelativeRect(bounds)
  const [labelWidth, setLabelWidth] = useState(0)

  const symbolWidth = canvasRelativeRect?.width ?? 0
  const symbolPositionX = canvasRelativeRect?.x ?? 0

  const canvasWidth = canvasContainerRef.current?.getBoundingClientRect().width

  /**
   * Getting the width of the symbol name for two purposes:
   * 1) To check if the name needs to overflow the symbol container
   * 2) To get the coordinates of the symbol on the X axis. This is used to
   * check if the symbol's name will overflow under the sidebar, and if it does,
   * we limit its width.
   */
  const { isLabelBiggerThanSymbol, symbolLabelMaxWidth } = useMemo(() => {
    return {
      isLabelBiggerThanSymbol: labelWidth > symbolWidth,
      symbolLabelMaxWidth:
        canvasWidth && symbolPositionX
          ? canvasWidth - symbolPositionX
          : undefined,
    }
  }, [labelWidth, symbolWidth, canvasWidth, symbolPositionX])

  // Hide the selector until it's ready
  if (isLoading || !canvasRelativeRect) {
    return null
  }

  const { x, y, width, height } = canvasRelativeRect

  return (
    <S.StyledLayerOverlay
      className={className}
      $borderSize={2}
      $color="purple"
      $width={width}
      $height={height}
      $x={x}
      $y={y}
      title={symbolName}
      $zIndex={OVERLAYS_Z_INDEX.SELECTED_SYMBOL_RECTANGLE}
    >
      <S.SymbolNameContainer isLabelBiggerThanSymbol={isLabelBiggerThanSymbol}>
        <ArtboardDetailInspectorSymbolLink
          permanentArtboardShortId={permanentArtboardShortId}
          symbolMaster={symbolMaster}
          shareIdentifier={shareIdentifier}
        >
          <S.SymbolLabel
            ref={(ref: HTMLDivElement) => {
              // Wait for ref to be ready,
              // We need to calculate the width when the html is ready in the DOM.
              setLabelWidth(ref?.clientWidth ?? 0)
            }}
            width={symbolLabelMaxWidth}
          >
            <TruncateWithTooltip>{symbolName}</TruncateWithTooltip>
            <S.GoToSymbolIcon />
          </S.SymbolLabel>
        </ArtboardDetailInspectorSymbolLink>
      </S.SymbolNameContainer>
    </S.StyledLayerOverlay>
  )
}
